import React, { useState, useEffect } from 'react';
import { ProjectRow } from '../../../lib/types';

interface WorkCategory {
  GLCodeId: string;
  Code: string;
  Description: string;
}

interface TimesheetRow {
  rowId: string;
  type: 'project' | 'category';
  projectId?: string;
  glCodeId?: string;
  title: string;
  hours: {
    Monday: number;
    Tuesday: number;
    Wednesday: number;
    Thursday: number;
    Friday: number;
    Saturday: number;
    Sunday: number;
  };
}

interface TimesheetGridProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function TimesheetGrid({ onSuccess, onCancel }: TimesheetGridProps) {
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [categories, setCategories] = useState<WorkCategory[]>([]);
  const [selectedRows, setSelectedRows] = useState<TimesheetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedType, setSelectedType] = useState<'project' | 'category'>('project');
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projRes, catRes] = await Promise.all([
        fetch('/projects'),
        fetch('/gl-codes'),
      ]);
      const projData = await projRes.json();
      const catData = await catRes.json();
      setProjects(projData.data || []);
      setCategories(catData.data || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const addRow = () => {
    if (!selectedId) {
      setError('Please select a project or work category');
      return;
    }

    const rowId = `${selectedType}-${selectedId}-${Date.now()}`;
    let title = '';

    if (selectedType === 'project') {
      const project = projects.find(p => p.ProjectId === selectedId);
      if (!project || selectedRows.find(r => r.projectId === selectedId)) {
        setError('Project already added or not found');
        return;
      }
      title = project.Title;
    } else {
      const category = categories.find(c => c.GLCodeId === selectedId);
      if (!category || selectedRows.find(r => r.glCodeId === selectedId)) {
        setError('Work category already added or not found');
        return;
      }
      title = category.Code;
    }

    setSelectedRows([
      ...selectedRows,
      {
        rowId,
        type: selectedType,
        projectId: selectedType === 'project' ? selectedId : undefined,
        glCodeId: selectedType === 'category' ? selectedId : undefined,
        title,
        hours: {
          Monday: 0,
          Tuesday: 0,
          Wednesday: 0,
          Thursday: 0,
          Friday: 0,
          Saturday: 0,
          Sunday: 0,
        },
      },
    ]);
    setSelectedId('');
    setError('');
  };

  const removeRow = (rowId: string) => {
    setSelectedRows(selectedRows.filter(r => r.rowId !== rowId));
  };

  const updateHours = (rowId: string, day: string, hours: number) => {
    setSelectedRows(
      selectedRows.map(r =>
        r.rowId === rowId
          ? { ...r, hours: { ...r.hours, [day]: hours } }
          : r
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedRows.length === 0) {
      setError('Please add at least one project or work category');
      return;
    }

    const totalHours = selectedRows.reduce(
      (sum, r) => sum + Object.values(r.hours).reduce((a, b) => a + b, 0),
      0
    );

    if (totalHours === 0) {
      setError('Please log some hours');
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      const weekStartDate = new Date().toISOString().slice(0, 10);
      const userEmail = localStorage.getItem('auth_email') || 'test@example.com';

      // Build entries for all rows (projects and categories mixed)
      const entries = selectedRows.flatMap(row =>
        DAYS.map(day => ({
          projectId: row.projectId,
          glCodeId: row.glCodeId,
          dayOfWeek: day,
          hoursLogged: row.hours[day as keyof typeof row.hours],
        })).filter(e => e.hoursLogged > 0)
      );

      const response = await fetch('/timesheets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          weekStartDate,
          entries,
          userEmail,
        }),
      });

      const data = await response.json();
      if (!data.success) {
        setError(`Failed to submit timesheet: ${data.error}`);
        setSubmitting(false);
        return;
      }

      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Error submitting timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onCancel}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="loading">Loading data...</div>
        </div>
      </div>
    );
  }

  const availableProjects = projects.filter(
    p => !selectedRows.find(r => r.projectId === p.ProjectId)
  );

  const availableCategories = categories.filter(
    c => !selectedRows.find(r => r.glCodeId === c.GLCodeId)
  );

  const totalHours = selectedRows.reduce(
    (sum, r) => sum + Object.values(r.hours).reduce((a, b) => a + b, 0),
    0
  );

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1100px', maxHeight: '90vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h2>Weekly Timesheet</h2>
          <button onClick={onCancel} className="btn-close">×</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {/* Add Row Section */}
            <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
              <h3 style={{ marginTop: 0 }}>Add Project or Work Category</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '1rem', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Type:</label>
                  <select
                    value={selectedType}
                    onChange={(e) => {
                      setSelectedType(e.target.value as 'project' | 'category');
                      setSelectedId('');
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  >
                    <option value="project">Project</option>
                    <option value="category">Work Category</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>Select:</label>
                  <select
                    value={selectedId}
                    onChange={(e) => setSelectedId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  >
                    <option value="">-- Select --</option>
                    {selectedType === 'project'
                      ? availableProjects.map(p => (
                          <option key={p.ProjectId} value={p.ProjectId}>{p.Title}</option>
                        ))
                      : availableCategories.map(c => (
                          <option key={c.GLCodeId} value={c.GLCodeId}>{c.Code}</option>
                        ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={addRow}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Add Row
                </button>
              </div>
              {error && <div style={{ color: '#dc3545', marginTop: '0.5rem', fontSize: '0.9rem' }}>{error}</div>}
            </div>

            {selectedRows.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#999' }}>
                <p>Add a project or work category above to log hours</p>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '2rem', overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.9rem',
                  }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #ddd', minWidth: '180px' }}>
                          Item (Project/Category)
                        </th>
                        {DAYS.map(day => (
                          <th
                            key={day}
                            style={{
                              padding: '0.75rem',
                              textAlign: 'center',
                              borderBottom: '2px solid #ddd',
                              minWidth: '70px',
                            }}
                          >
                            {day.slice(0, 3)}
                          </th>
                        ))}
                        <th style={{
                          padding: '0.75rem',
                          textAlign: 'center',
                          borderBottom: '2px solid #ddd',
                          fontWeight: 600,
                        }}>
                          Total
                        </th>
                        <th style={{ padding: '0.75rem', minWidth: '70px' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRows.map(row => {
                        const rowTotal = Object.values(row.hours).reduce((a, b) => a + b, 0);
                        return (
                          <tr key={row.rowId} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '0.75rem', fontWeight: 500 }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '0.25rem 0.5rem',
                                backgroundColor: row.type === 'project' ? '#e3f2fd' : '#f3e5f5',
                                borderRadius: '3px',
                                fontSize: '0.85rem',
                                marginRight: '0.5rem',
                              }}>
                                {row.type === 'project' ? '📋' : '📌'} {row.type === 'project' ? 'Project' : 'Category'}
                              </span>
                              {row.title}
                            </td>
                            {DAYS.map(day => (
                              <td key={day} style={{ padding: '0.5rem', textAlign: 'center' }}>
                                <input
                                  type="number"
                                  min="0"
                                  max="24"
                                  step="0.5"
                                  value={row.hours[day as keyof typeof row.hours]}
                                  onChange={(e) =>
                                    updateHours(row.rowId, day, parseFloat(e.target.value) || 0)
                                  }
                                  style={{
                                    width: '100%',
                                    padding: '0.4rem',
                                    border: '1px solid #ddd',
                                    borderRadius: '3px',
                                    textAlign: 'center',
                                  }}
                                />
                              </td>
                            ))}
                            <td style={{
                              padding: '0.75rem',
                              textAlign: 'center',
                              fontWeight: 600,
                              background: '#f9f9f9',
                            }}>
                              {rowTotal}h
                            </td>
                            <td style={{ padding: '0.75rem' }}>
                              <button
                                type="button"
                                onClick={() => removeRow(row.rowId)}
                                style={{
                                  background: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.4rem 0.8rem',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                }}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      <tr style={{ background: '#e8f4f8', fontWeight: 600 }}>
                        <td style={{ padding: '0.75rem' }}>WEEKLY TOTAL</td>
                        {DAYS.map(day => {
                          const dayTotal = selectedRows.reduce(
                            (sum, r) => sum + r.hours[day as keyof typeof r.hours],
                            0
                          );
                          return (
                            <td
                              key={day}
                              style={{
                                padding: '0.75rem',
                                textAlign: 'center',
                                borderTop: '2px solid #ddd',
                              }}
                            >
                              {dayTotal}h
                            </td>
                          );
                        })}
                        <td style={{
                          padding: '0.75rem',
                          textAlign: 'center',
                          borderTop: '2px solid #ddd',
                          background: '#fff',
                          fontSize: '1.1rem',
                        }}>
                          {totalHours}h
                        </td>
                        <td />
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                  <p>Week of {new Date().toISOString().slice(0, 10)}</p>
                </div>
              </>
            )}

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting || selectedRows.length === 0}
              >
                {submitting ? 'Submitting...' : 'Submit Timesheet'}
              </button>
              <button type="button" className="btn-secondary" onClick={onCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
